<template>
  <div>
    <!-- Loading skeletons — initial load only (no previous data) -->
    <div v-if="isLoading && !eventsResponse?.results.length">
      <div v-for="i in 8" :key="i" class="skeleton-row" />
    </div>

    <!-- Error state -->
    <div
      v-else-if="error && !isLoading"
      style="
        padding: 60px 16px;
        text-align: center;
        font-family: var(--mono);
        font-size: 12px;
        letter-spacing: 0.12em;
        text-transform: uppercase;
      "
    >
      <div style="color: var(--danger); margin-bottom: 16px">
        Impossible de charger les événements
      </div>
      <button class="btn" @click="emit('refresh')">Réessayer</button>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="!isLoading && eventsResponse && eventsResponse.count === 0"
      style="
        padding: 60px 16px;
        text-align: center;
        color: var(--ink-dim);
        font-family: var(--mono);
        font-size: 12px;
        letter-spacing: 0.12em;
        text-transform: uppercase;
      "
    >
      Aucun résultat — élargissez vos critères
    </div>

    <!-- Results -->
    <div v-else-if="eventsResponse">
      <!-- Event rows grouped by month -->
      <template v-for="(group, gIdx) in groupedEvents" :key="gIdx">
        <div class="month-sep">
          <h2>{{ group.month }}</h2>
          <span class="count"
            >{{ group.events.length }} événement{{
              group.events.length > 1 ? 's' : ''
            }}</span
          >
          <div class="divider" />
        </div>
        <EventThumb
          v-for="event in group.events"
          :key="event._id"
          :event="event"
          @refresh-events-list="emit('refresh')"
        />
      </template>

      <!-- Pagination -->
      <div v-if="eventsResponse.count > pageSize" class="pagination">
        <button
          v-if="currentPage > 1"
          class="btn"
          style="padding: 0; width: 32px; height: 32px; border-radius: 2px"
          @click="goToPage(1)"
        >
          «
        </button>
        <button
          v-if="currentPage > 1"
          class="btn"
          style="padding: 0; width: 32px; height: 32px; border-radius: 2px"
          @click="goToPage(currentPage - 1)"
        >
          ‹
        </button>

        <template v-for="page in visiblePages" :key="page">
          <span v-if="page === '...'" class="pagination-ellipsis">…</span>
          <button
            v-else
            :class="{ active: page === currentPage }"
            @click="goToPage(page as number)"
          >
            {{ page }}
          </button>
        </template>

        <button
          v-if="currentPage < totalPages"
          class="btn"
          style="padding: 0; width: 32px; height: 32px; border-radius: 2px"
          @click="goToPage(currentPage + 1)"
        >
          ›
        </button>
        <button
          v-if="currentPage < totalPages"
          class="btn"
          style="padding: 0; width: 32px; height: 32px; border-radius: 2px"
          @click="goToPage(totalPages)"
        >
          »
        </button>
      </div>
    </div>

    <!-- Scroll to top -->
    <button
      v-if="showScrollTop"
      class="scroll-top-btn"
      aria-label="Scroll to top"
      @click="scrollToTop"
    >
      ↑
    </button>
  </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs';
import { useWindowScroll } from '@vueuse/core';
import type { EventItem } from '@/types/Event';

interface FetchEventResponse {
  results: EventItem[];
  count: number;
}

const PAGE_SIZES = [10, 16, 20];

const props = defineProps<{
  offset: number;
  eventsResponse: FetchEventResponse | null;
  isLoading: boolean;
  pageSize: number;
  error?: Error | null;
}>();

const emit = defineEmits<{
  refresh: [];
  pageUpdate: [event: { page: number; rows: number }];
}>();

// ── Pagination ──────────────────────────────────────────────
const currentPage = computed(
  () => Math.floor(props.offset / props.pageSize) + 1,
);
const totalPages = computed(() =>
  Math.ceil((props.eventsResponse?.count ?? 0) / props.pageSize),
);

const visiblePages = computed(() => {
  const total = totalPages.value;
  const cur = currentPage.value;
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | '...')[] = [];
  if (cur <= 4) {
    pages.push(1, 2, 3, 4, 5, '...', total);
  } else if (cur >= total - 3) {
    pages.push(1, '...', total - 4, total - 3, total - 2, total - 1, total);
  } else {
    pages.push(1, '...', cur - 1, cur, cur + 1, '...', total);
  }
  return pages;
});

const goToPage = (page: number) => {
  emit('pageUpdate', { page: page - 1, rows: props.pageSize });
};

// ── Month grouping ───────────────────────────────────────────
const groupedEvents = computed(() => {
  if (!props.eventsResponse?.results.length) return [];
  const map = new Map<string, { month: string; events: EventItem[] }>();
  for (const event of props.eventsResponse.results) {
    const key = dayjs.utc(event.startDate).format('YYYY-MM');
    const label = dayjs.utc(event.startDate).format('MMMM YYYY').toUpperCase();
    if (!map.has(key)) map.set(key, { month: label, events: [] });
    map.get(key)!.events.push(event);
  }
  return Array.from(map.values());
});

// ── Scroll-to-top ─────────────────────────────────────────────
const { y } = useWindowScroll();
const showScrollTop = computed(() => y.value > 400);

const scrollToTop = () => {
  y.value = 0;
};

// Keep PAGE_SIZES available for parent via expose (not needed here but keeps compat)
defineExpose({ PAGE_SIZES });
</script>
